import React from 'react';
import { ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';

const QuestionTrafficLight = ({ data, tooltipContainerRef }) => {
    // Get all status percentages from the data
    // Using default values of 0 if props are not provided or are NaN
    const unknown = !isNaN(data?.unknown) ? data.unknown : 0;
    const no = !isNaN(data?.no) ? data.no : 0;
    const yes = !isNaN(data?.yes) ? data.yes : 0;
    
    // Handle cases where percentages might not sum to exactly 100 due to rounding
    const rawTotal = unknown + no + yes;
    let adjustedUnknown = unknown;
    let adjustedNo = no;
    let adjustedYes = yes;
    
    // If the total is not 100, adjust the largest value to make it 100
    if (rawTotal !== 0 && rawTotal !== 100) {
        const max = Math.max(unknown, no, yes);
        const diff = 100 - rawTotal;
        
        if (max === unknown) adjustedUnknown += diff;
        else if (max === no) adjustedNo += diff;
        else adjustedYes += diff;
    }
    
    // Get absolute counts directly from data with safeguards against undefined values
    const totalCount = data?.total || 0;
    const unknownCount = data?.unknownCount || 0;
    const noCount = data?.noCount || 0;
    const yesCount = data?.yesCount || 0;

    // Stable Tooltip IDs
    const tooltipIds = {
        unknown: 'qtl-tooltip-unknown',
        no: 'qtl-tooltip-no',
        yes: 'qtl-tooltip-yes',
        noResponses: 'qtl-tooltip-no-responses',
    };

    // If no responses or all values are 0, show empty progress bar
    if (totalCount === 0 || rawTotal === 0) {
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id={tooltipIds.noResponses}>
                        Keine Antworten vorhanden
                    </Tooltip>
                }
                container={tooltipContainerRef?.current}
            >
                <ProgressBar style={{ height: '24px' }}>
                    <ProgressBar variant="secondary" now={100} key="empty-bar-segment" />
                </ProgressBar>
            </OverlayTrigger>
        );
    }

    // Custom colors for each response type
    const unknownColor = "var(--status-no-status)";
    const noColor = "var(--status-negative)";
    const yesColor = "var(--status-positive)";

    return (
        <div className="progress" style={{ height: '24px' }}>
            {/* Order as specified: keine Angabe, nein, ja */}
            {adjustedUnknown > 0 && (
                <OverlayTrigger
                    key="unknown-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.unknown}>
                            Keine Angabe: {unknown}% ({unknownCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjustedUnknown}%`, backgroundColor: unknownColor }}
                        aria-valuenow={adjustedUnknown}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjustedNo > 0 && (
                <OverlayTrigger
                    key="no-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.no}>
                            Nein: {no}% ({noCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjustedNo}%`, backgroundColor: noColor }}
                        aria-valuenow={adjustedNo}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjustedYes > 0 && (
                <OverlayTrigger
                    key="yes-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.yes}>
                            Ja: {yes}% ({yesCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjustedYes}%`, backgroundColor: yesColor }}
                        aria-valuenow={adjustedYes}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
        </div>
    );
};

export default QuestionTrafficLight;
